import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  listAllOutlooks,
  generateWeeklyOutlookDraft,
  saveOutlookDraft,
  approveOutlook,
  type WeeklyOutlook,
} from "@/lib/weekly-outlook.functions";
import { WeeklyOutlookView } from "@/components/weekly-outlook/WeeklyOutlookView";

export const Route = createFileRoute("/_authenticated/admin/weekly-outlook")({
  validateSearch: z.object({ slug: z.string().optional() }),
  head: () => ({ meta: [{ title: "Weekly Outlook Review | Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminWeeklyOutlook,
});

function AdminWeeklyOutlook() {
  const { slug } = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const list = useServerFn(listAllOutlooks);
  const gen = useServerFn(generateWeeklyOutlookDraft);
  const { data = [] } = useQuery({ queryKey: ["admin-weekly-outlooks"], queryFn: () => list() });
  const [busy, setBusy] = useState(false);
  const selected = data.find((o) => o.slug === slug) ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Unlocks at the Friday 21:00 UTC close of the most recent completed week.
  const unlockAt = (() => {
    const d = new Date(now);
    const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dow = day.getUTCDay();
    const back = dow === 6 ? 1 : dow === 0 ? 2 : dow + 2;
    const fri = new Date(day.getTime() - back * 86400000);
    return fri.getTime() + 21 * 3600 * 1000;
  })();
  const remaining = Math.max(0, unlockAt - now);
  const locked = remaining > 0;
  const countdown = (() => {
    const s = Math.floor(remaining / 1000);
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return `${d}d ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
  })();

  async function generate() {
    setBusy(true);
    try {
      const r = await gen();
      await qc.invalidateQueries({ queryKey: ["admin-weekly-outlooks"] });
      navigate({ search: { slug: r.slug } });
      toast.success("Draft generated. Telegram notice sent to admins.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Generation failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Weekly Outlook</h1>
          <p className="text-sm text-muted-foreground">Draft, review and approve the weekly report.</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button onClick={generate} disabled={busy || locked}>
            {busy ? "Generating" : locked ? `Locked · ${countdown}` : "Generate week ahead draft"}
          </Button>
          {locked && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              Unlocks {new Date(unlockAt).toISOString().replace("T", " ").slice(0, 16)} UTC
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.map((o) => (
          <button
            key={o.slug}
            onClick={() => navigate({ search: { slug: o.slug } })}
            className={`rounded-md border px-3 py-1.5 font-mono text-xs ${o.slug === slug ? "border-gold text-gold" : "border-border/60 text-muted-foreground"}`}
          >
            {o.week_start} · {o.status}
          </button>
        ))}
        {data.length === 0 && <p className="text-sm text-muted-foreground">No reports yet.</p>}
      </div>
      {selected && <Editor key={selected.slug + selected.status} o={selected} />}
    </div>
  );
}

function Editor({ o }: { o: WeeklyOutlook }) {
  const qc = useQueryClient();
  const save = useServerFn(saveOutlookDraft);
  const approve = useServerFn(approveOutlook);
  const [f, setF] = useState({
    title: o.title,
    summary: o.summary,
    regime: (o.regime as "bull" | "bear" | "range") ?? "range",
    retrospective: o.content.retrospective,
    outlook: o.content.outlook,
    levels: o.content.levels,
    riskNote: o.content.riskNote,
    bullCase: o.content.bullCase ?? "",
    bearCase: o.content.bearCase ?? "",
    catalysts: (o.content.catalysts ?? []).map((c) => ({ ...c })),
  });
  const published = o.status === "published";
  const preview: WeeklyOutlook = { ...o, title: f.title, summary: f.summary, regime: f.regime, content: { ...o.content, ...f } };
  const setCat = (i: number, k: "forecast" | "previous", v: string) =>
    setF({ ...f, catalysts: f.catalysts.map((c, j) => (j === i ? { ...c, [k]: v || null } : c)) });

  async function doSave() {
    await save({ data: { slug: o.slug, ...f } });
    await qc.invalidateQueries({ queryKey: ["admin-weekly-outlooks"] });
  }
  async function onSave() {
    try { await doSave(); toast.success("Saved"); } catch (e) { toast.error(e instanceof Error ? e.message : "Save failed"); }
  }
  async function onApprove() {
    try {
      await doSave();
      await approve({ data: { slug: o.slug } });
      await qc.invalidateQueries({ queryKey: ["admin-weekly-outlooks"] });
      toast.success("Published");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Approval failed"); }
  }

  const area = (k: "retrospective" | "outlook" | "levels" | "riskNote" | "bullCase" | "bearCase", label: string) => (
    <label className="block space-y-1">
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <Textarea rows={5} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} disabled={published} />
    </label>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4 rounded-xl border border-border/60 bg-card p-5">
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} disabled={published} />
        <Input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} disabled={published} />
        <div className="flex gap-2">
          {(["bull", "bear", "range"] as const).map((r) => (
            <button key={r} disabled={published} onClick={() => setF({ ...f, regime: r })} className={`rounded border px-3 py-1 font-mono text-xs uppercase ${f.regime === r ? "border-gold text-gold" : "border-border/60 text-muted-foreground"}`}>{r}</button>
          ))}
        </div>
        {o.content.analyst?.error && <p className="text-xs text-destructive">Analyst: {o.content.analyst.error}. Sections need manual drafting.</p>}
        {f.catalysts.length > 0 && (
          <div className="space-y-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Catalysts: forecast and prior</span>
            {f.catalysts.map((c, i) => (
              <div key={c.title + c.date} className="grid grid-cols-[1fr_6rem_6rem] items-center gap-2">
                <span className="truncate text-xs">{c.date.slice(5, 16).replace("T", " ")} {c.title}</span>
                <Input placeholder="Forecast" value={c.forecast ?? ""} onChange={(e) => setCat(i, "forecast", e.target.value)} disabled={published} />
                <Input placeholder="Prior" value={c.previous ?? ""} onChange={(e) => setCat(i, "previous", e.target.value)} disabled={published} />
              </div>
            ))}
          </div>
        )}
        {area("outlook", "Week ahead")}
        {area("bullCase", "Bullish scenario")}
        {area("bearCase", "Bearish scenario")}
        {area("levels", "Key levels")}
        {area("riskNote", "Execution notes")}
        {area("retrospective", "Prior week review")}
        {published ? (
          <Link to="/weekly-outlook/$slug" params={{ slug: o.slug }} className="text-sm text-gold underline">Published. Open public page</Link>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={onSave}>Save draft</Button>
            <Button onClick={onApprove}>Approve and publish</Button>
          </div>
        )}
      </div>
      <div><WeeklyOutlookView o={preview} /></div>
    </div>
  );
}
